export interface UserBalance {
    walletId: string;
    walletBalance: number;
    currency: string;
}

export interface UserDelete {
    userId: number;
    individualId?: number;
    email?: string;
    reason: string;
}

export interface RiskCountryOverride {
    id: number;
    individualId: number;
    enrollCountryCodeOverride: string;
    ipCountryCodeOverride: string;
    geoLocationCountryCodeOverride: string;
    reason: string;
    isInitialSnapshot: boolean;
    createdByOperatorId: number | null;
    createdByOperatorEmail: string;
    createdAt: Date;
}

/**
 * The PEP declaration the customer made during onboarding (Users.PepDeclarationJson on the CRM
 * side). Read-only: this is what the customer stated, not the risk engine's screening result
 * (isPEPCalculated) nor compliance's final decision (isPEP).
 */
export interface PepDeclaration {
    /** Raw code: 001 = is a PEP, 002 = related to a PEP, 999 = neither. */
    pepMemberType?: string | null;
    /** Raw relation code, set only when pepMemberType is 002. */
    pepRelation?: string | null;
    /** Raw PEP type code, set only when pepMemberType is 001. */
    pepType?: string | null;
    pepMemberTypeLabel?: string | null;
    pepRelationLabel?: string | null;
    pepTypeLabel?: string | null;
}

export interface User {
    id: number;
    userId: number;
    firstName: string;
    middleName: string | null;
    lastName: string;
    nonLatinFirstName: string;
    nonLatinMiddleName: string | null;
    nonLatinLastName: string;
    photo: string;
    birthDate: Date;
    email: string;
    isEmailVerified: boolean;
    phoneNumber: string;
    isPhoneNumberVerified: boolean;
    walletId: string | null;
    walletBalance: number | null;
    currency: string;
    flexCubeBranch: string;
    merchant: string;
    merchantGroup: string;
    lastLogin: Date;
    createdTimeStamp: Date;
    active: boolean;
    status: IndividualStatus;
    userStatus: IndividualStatus;
    kycStatus?: number;
    kycLevel: number;
    kycVerified: boolean;
    language: string;
    comments: string;
    tariff: string;
    primaryLanguage: string;
    privacyPolicyAcceptanceDate: Date;
    registerationStamp: Date;
    residenceAddress: UserAddress;
    secondaryLanguage: string;
    sourceOfFunds: string;
    tariffAcceptanceDate: Date;
    taxResidences: string;
    termsAndConditionsAcceptanceDate: Date;
    correspondenceAddress: UserAddress;
    nationalIdNumber: string;
    nationalities: string;
    sourceOfFundId: number;
    merchantId: number;
    merchantGroupId: number;
    kycLevelId?: number;
    cbsOnboarded: boolean;
    cbsId?: string;
    cryptoCustomer: boolean;
    accountPurposeCode: string;
    mainActivityCode: string;
    financingMethodCode: string;
    documentsAuthenticatedByThirdParty: boolean;
    enrollIP: string;
    enrollLatitude: number;
    enrollLongitude: number;
    enrollCountryCode: string;
    ipCountryCode: string;
    geoLocationCountryCode: string;
    riskCountryOverrideHistory?: RiskCountryOverride[];
    currentRiskCountryOverride?: RiskCountryOverride;
    isPEP?: boolean;
    isSIP?: boolean;
    isPEPCalculated?: boolean;
    isSIPCalculated?: boolean;
    isPepFormSubmitted?: boolean | null;
    pepDeclaration?: PepDeclaration | null;
    riskScore: number;
    riskGrade: number;
    riskEvaluationStatus: number;
    riskAction: number;
    personalIdentificationNumber: string;
    gender: string;
    kycSubmissionId: number;
    additionalData: UserAdditionalData;
}

export interface UserAdditionalData {
    type: string;
    data: UserAdditionalDataFields;
}

interface UserAdditionalDataFields {
    firstName: string;
    lastName: string;
    pin: string;
}

export enum AdditionalDataEnum {
    FastpayAdditionalData = "FastpayAdditionalData",
    EnrollAdditionalData = "EnrollAdditionalData",
}

export interface UpdateUserProfileRequest {
    userId: number;
    additionalData: UpdateUserAdditionalData;
}

export interface UpdateUserAdditionalData {
    type: string;
    data: UpdateUserAdditionalDataFields;
}

interface UpdateUserAdditionalDataFields {
    firstName: string;
    lastName: string;
}

export enum IndividualStatus {
    Registered = 50,
    New = 100,
    PendingVerification = 150,
    Active = 200,
    Deactivated = 300,
    Closed = 350,
    Blocked = 400,
}

export interface OperatorDataModel {
    id: number;
    userId: number;
    firstName: string;
    flexCubeBranch: string;
    lastName: string;
    merchantId: string;
    password: string;
    merchantName: string;
    email: string;
    isEmailVerified: boolean;
    isPhoneNumberVerified: boolean;
    phoneNumber: string;
    photoStorageId: string;
    photo: string;
    status: number;
    lastLogin: Date;
    userRoles: number[];
    // TNPDEVAPPS-1812: whether this operator may perform cross-merchant operations.
    // Only settable for Tenn Payments ("TNP") operators; enforced server-side (see CRM OperatorService).
    allowedCrossMerchantOperations?: boolean;
}

export interface UserProfileUpdate {
    id: number;
    firstName: string;
    middleName?: string;
    lastName: string;
    nonLatinFirstName: string;
    nonLatinMiddleName?: string;
    nonLatinLastName: string;
    email: string;
    photo: string;
    birthDate: Date;
    phoneNumber: string;
    cryptoCustomer: boolean;
    sourceOfFundId: number;
    residenceAddress: UserAddress;
    correspondenceAddress: UserAddress;
    nationalities: string;
    taxResidences: string;
    nationalIdNumber: string;
    primaryLanguage: string;
    secondaryLanguage: string;
    merchantGroupId: number;
    accountPurposeCode: string;
    mainActivityCode: string;
    financingMethodCode: string;
    personalIdentificationNumber: string;
    isPEP?: boolean;
    isSIP?: boolean;
    gender: string;
    minimumMatchingPercentageSIP: number;
    minimumMatchingPercentagePEP: number;
    enrollCountryCodeOverride?: string;
    ipCountryCodeOverride?: string;
    geoLocationCountryCodeOverride?: string;
    riskCountryOverrideReason?: string;
}

export class UserAddress {
    addressLine: string = "";
    city: string = "";
    zipCode: string = "";
    countryId: string = "";
    stateProvinceCounty: string = "";
    type?: string = "";
}

export interface UserPaymentMethod {
    paymentType: string;
    value: string;
    ownerName: string;
    lastFundsDeposited: number;
    lastFundsWithdrawn: number;
    has2FAOr3DS: boolean;
}

export interface UpdateUserPhotoRequest {
    userId: number;
    photo: string;
    userType: UserTypeRelation;
}

export enum UserTypeRelation {
    Individual = 1,
    Operator = 3,
}
